import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import type { AppRole } from '@/types/database';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: AppRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, role, loading, profile } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change if required
  if (profile?.must_change_password) {
    if (location.pathname !== '/first-access-password') {
      return <Navigate to="/first-access-password" replace />;
    }
  }

  // Prevent access to first-access page if not required
  if (!profile?.must_change_password && location.pathname === '/first-access-password') {
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles && role && !allowedRoles.includes(role)) {
    // Redirect based on user role
    if (role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (role === 'mentor') {
      return <Navigate to="/mentor" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Each route gets its own ErrorBoundary, keyed by pathname so it auto-resets on navigation
  return (
    <ErrorBoundary resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}
